import React, { useState, useEffect } from 'react';
import { FiSettings, FiSave, FiAlertCircle, FiCheckCircle, FiKey, FiDatabase, FiEye, FiEyeOff } from 'react-icons/fi';
import { services } from '../../../../services/api';
import useBreakpoint from '../../../../hooks/useBreakpoint';

const ExchangeSettingsPanel = () => {
    //! Phone gets CARDS, not the table. This panel is nested (page px-4 + the
    //! parent dashboard's own padding + this card's padding), leaving ~295px of
    //! usable width at 375px. Columns 2-3 hold selects whose intrinsic width
    //! comes from their longest option ("ICICI Direct - Some Long Name"), so the
    //! selects were wider than the window: the user scrolled sideways INSIDE a
    //! sideways-scrolling table. Desktop keeps the table.
    const { isPhone } = useBreakpoint();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [exchanges, setExchanges] = useState([]);
    const [apiCredentials, setApiCredentials] = useState([]);
    const [availableAccounts, setAvailableAccounts] = useState([]);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [expandedSection, setExpandedSection] = useState('exchanges'); // 'exchanges' or 'api-keys'
    const [showApiKeys, setShowApiKeys] = useState({}); // Track which API keys are visible
    const [decryptedKeys, setDecryptedKeys] = useState({}); // Store decrypted keys

    // Fetch settings on mount
    useEffect(() => {
        fetchSettings();
        fetchBrokerAccounts();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await services.exchangeStatus.settings.getOverview();
            
            setExchanges(response.data.exchanges || []);
            setApiCredentials(response.data.api_credentials || []);
        } catch (err) {
            console.error('Failed to fetch settings:', err);
            setError('Failed to load settings. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fetchBrokerAccounts = async () => {
        try {
            // Use existing broker accounts API that's already authenticated
            const response = await services.brokerAccounts.getAccounts();
            console.log('Broker accounts response:', response);
            
            // The response might be an array directly or wrapped in data
            const accountsArray = Array.isArray(response) ? response : (response.data || []);
            console.log('Accounts array:', accountsArray);
            
            // Map broker IDs to names (hardcoded for now, could be fetched from API)
            const brokerNames = {
                1: 'ICICI Direct',
                2: 'Flattrade',
                // Add more as needed
            };
            
            // Transform to format needed for dropdown
            const accounts = accountsArray.map(acc => {
                console.log('Processing account:', acc);
                return {
                    account_id: acc.account_id || acc.id, // Use account_id from response
                    broker_id: acc.broker_id,
                    broker_name: brokerNames[acc.broker_id] || acc.broker?.name || acc.broker_name || `Broker ${acc.broker_id}`,
                    broker_user_id: acc.broker_user_id,
                    account_holder_name: acc.account_holder_name || acc.broker_user_name,
                    status: acc.status
                };
            }).filter(acc => acc.status === 'active' || acc.status === 'verified');
            
            console.log('Transformed accounts:', accounts);
            setAvailableAccounts(accounts);
        } catch (err) {
            console.error('Failed to fetch broker accounts:', err);
            // Don't show error to user, just log it
        }
    };

    const handleToggleApiKey = async (provider) => {
        // If already showing, just hide it
        if (showApiKeys[provider]) {
            setShowApiKeys(prev => ({ ...prev, [provider]: false }));
            return;
        }

        // If not decrypted yet, fetch it
        if (!decryptedKeys[provider]) {
            try {
                // You'll need to create an endpoint to fetch the decrypted key
                const response = await services.exchangeStatus.settings.getDecryptedAPIKey(provider);
                setDecryptedKeys(prev => ({ ...prev, [provider]: response.data.api_key }));
            } catch (err) {
                console.error('Failed to fetch decrypted key:', err);
                setError(`Failed to load ${provider} API key`);
                return;
            }
        }

        // Show the key
        setShowApiKeys(prev => ({ ...prev, [provider]: true }));
    };

    const handleExchangeUpdate = async (exchangeCode, field, value) => {
        // Update local state immediately for better UX
        setExchanges(prev => prev.map(ex => 
            ex.exchange_code === exchangeCode 
                ? { ...ex, [field]: value }
                : ex
        ));
    };

    const saveExchangeConfig = async (exchangeCode) => {
        try {
            setSaving(true);
            setError(null);
            
            const exchange = exchanges.find(ex => ex.exchange_code === exchangeCode);
            
            // Map broker_id to broker_type for backend validation
            const brokerTypeMap = {
                1: 'icici',
                2: 'flattrade',
            };
            
            const payload = {
                validation_method: exchange.validation_method,
                broker_account_id: exchange.broker_account_id || null,
                broker_type: exchange.broker_account_id 
                    ? (brokerTypeMap[availableAccounts.find(a => a.account_id === exchange.broker_account_id)?.broker_id] || null)
                    : null,
                api_provider: exchange.api_provider || null,
                enabled: exchange.enabled
            };
            
            console.log('Saving exchange config:', exchangeCode, payload);
            
            await services.exchangeStatus.settings.updateExchangeConfig(exchangeCode, payload);
            
            setSuccess(`${exchangeCode} configuration saved successfully!`);
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error('Failed to save config:', err);
            setError(`Failed to save ${exchangeCode} configuration. ${err.response?.data?.message || err.message || ''}`);
        } finally {
            setSaving(false);
        }
    };

    const handleAPIKeyUpdate = async (provider, apiKey) => {
        try {
            setSaving(true);
            setError(null);
            
            await services.exchangeStatus.settings.updateAPICredential({
                provider,
                api_key: apiKey
            });
            
            // Update local state
            setApiCredentials(prev => prev.map(cred =>
                cred.provider === provider
                    ? { ...cred, is_configured: true, validation_status: 'not_tested' }
                    : cred
            ));
            
            setSuccess(`${provider} API key saved successfully!`);
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error('Failed to save API key:', err);
            setError(`Failed to save ${provider} API key. ${err.message || ''}`);
        } finally {
            setSaving(false);
        }
    };

    //--> The per-method configuration control. Shared by the desktop table and the
    //    phone cards so the two presentations can't drift; only the select's width
    //    class differs (the table keeps its intrinsic width, cards go full-width).
    const renderConfigControl = (exchange, selectClass) => (
        <>
            {exchange.validation_method === 'broker_api' && (
                <select
                    value={exchange.broker_account_id || ''}
                    onChange={(e) => {
                        handleExchangeUpdate(exchange.exchange_code, 'broker_account_id', e.target.value);
                    }}
                    className={selectClass}
                >
                    <option value="">Select Account</option>
                    {availableAccounts.map((account) => (
                        <option key={account.account_id} value={account.account_id}>
                            {account.broker_name} - {account.account_holder_name || account.broker_user_id || account.account_id}
                        </option>
                    ))}
                </select>
            )}
            {exchange.validation_method === 'global_api' && (
                <select
                    value={exchange.api_provider || ''}
                    onChange={(e) => handleExchangeUpdate(exchange.exchange_code, 'api_provider', e.target.value)}
                    className={selectClass}
                >
                    <option value="">Select Provider</option>
                    <option key="polygon" value="polygon">Polygon.io</option>
                    <option key="alpha_vantage" value="alpha_vantage">Alpha Vantage</option>
                    <option key="tradier" value="tradier">Tradier</option>
                </select>
            )}
            {exchange.validation_method === 'calendar_only' && (
                <span className="text-sm text-fg-2 italic">
                    No configuration needed
                </span>
            )}
        </>
    );

    //--> Enabled switch. On cards the label is dropped: the card heading already
    //    carries an Enabled/Disabled badge, so the text would be said twice.
    const renderEnabledToggle = (exchange, showLabel) => (
        <label className="relative inline-flex items-center cursor-pointer">
            <input
                type="checkbox"
                checked={exchange.enabled}
                onChange={(e) => handleExchangeUpdate(exchange.exchange_code, 'enabled', e.target.checked)}
                className="sr-only peer"
            />
            <div className="w-11 h-6 bg-bg-3 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent/30 rounded-full peer dark:bg-bg-3 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[var(--divider)] after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-[var(--divider)] peer-checked:bg-accent"></div>
            {showLabel && (
                <span className="ml-3 text-sm font-medium text-fg-1">
                    {exchange.enabled ? 'Enabled' : 'Disabled'}
                </span>
            )}
        </label>
    );

    //--> PHONE: one card per exchange. The key fix is that the selects go
    //    full-width — in the table their intrinsic width came from their longest
    //    option, so they overflowed a window that was already overflowing.
    const renderExchangeCards = () => (
        <div className="flex flex-col gap-3">
            {exchanges.map((exchange) => (
                <div key={exchange.exchange_code} className="rounded-lg border border-[var(--divider)] bg-bg-2 p-4">
                    <div className="flex items-center justify-between gap-3">
                        <div className="truncate text-sm font-semibold text-fg-1">
                            {exchange.exchange_code}
                        </div>
                        <span className={`ds-badge sm ${exchange.enabled ? 'profit' : 'neutral'} shrink-0`}>
                            {exchange.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                    </div>

                    <div className="mt-3 flex flex-col gap-3 border-t border-[var(--divider)] pt-3">
                        <label className="block">
                            <span className="mb-1 block text-xs font-semibold text-fg-3">Validation Method</span>
                            <select
                                value={exchange.validation_method}
                                onChange={(e) => handleExchangeUpdate(exchange.exchange_code, 'validation_method', e.target.value)}
                                className="ds-input block w-full"
                            >
                                <option value="broker_api">Broker API</option>
                                <option value="global_api">Global API</option>
                                <option value="calendar_only">Calendar Only</option>
                            </select>
                        </label>

                        <div>
                            <span className="mb-1 block text-xs font-semibold text-fg-3">Configuration</span>
                            {renderConfigControl(exchange, 'ds-input block w-full')}
                        </div>

                        <div className="flex items-center justify-between gap-3">
                            <span className="text-xs font-semibold text-fg-3">Status</span>
                            {renderEnabledToggle(exchange, false)}
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={() => saveExchangeConfig(exchange.exchange_code)}
                        disabled={saving}
                        className="btn btn-primary mt-4 inline-flex w-full items-center justify-center gap-2"
                    >
                        <FiSave />
                        Save
                    </button>
                </div>
            ))}
        </div>
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
                <span className="ml-3 text-fg-2">Loading settings...</span>
            </div>
        );
    }

    return (
        // p-4 below sm: this panel's padding compounds with the page's px-4 AND the
        // parent dashboard's own padding, which left only ~295px usable at 375px.
        <div className="bg-bg-2 rounded-lg shadow-sm border border-[var(--divider)] p-4 sm:p-6">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                    <FiSettings className="text-2xl text-accent mr-3" />
                    <div>
                        <h3 className="ds-h3 text-fg-1">
                            Exchange Validation Settings
                        </h3>
                        <p className="text-sm text-fg-2">
                            Configure how each exchange validates market status
                        </p>
                    </div>
                </div>
            </div>

            {/* Success/Error Messages */}
            {success && (
                <div className="mb-4 p-3 bg-[var(--badge-profit-bg)] border border-[var(--divider)] rounded-lg flex items-center">
                    <FiCheckCircle className="text-[var(--badge-profit-fg)] mr-2 flex-shrink-0" />
                    <span className="text-sm text-[var(--badge-profit-fg)]">{success}</span>
                </div>
            )}

            {error && (
                <div className="mb-4 p-3 bg-[var(--badge-loss-bg)] border border-[var(--divider)] rounded-lg flex items-center">
                    <FiAlertCircle className="text-[var(--badge-loss-fg)] mr-2 flex-shrink-0" />
                    <span className="text-sm text-[var(--badge-loss-fg)]">{error}</span>
                </div>
            )}

            {/* Section Tabs — the two labels total ~346px, which does not fit the
                ~295px usable at 375px, so the second tab was clipped with no
                affordance. Scroll horizontally instead, and don't let the labels
                wrap mid-word. */}
            <div className="flex space-x-4 mb-6 border-b border-[var(--divider)] overflow-x-auto">
                <button
                    onClick={() => setExpandedSection('exchanges')}
                    className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors whitespace-nowrap shrink-0 ${
                        expandedSection === 'exchanges'
                            ? 'border-accent text-accent'
                            : 'border-transparent text-fg-2 hover:text-fg-1'
                    }`}
                >
                    <FiDatabase className="inline mr-2" />
                    Exchange Configuration
                </button>
                <button
                    onClick={() => setExpandedSection('api-keys')}
                    className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors whitespace-nowrap shrink-0 ${
                        expandedSection === 'api-keys'
                            ? 'border-accent text-accent'
                            : 'border-transparent text-fg-2 hover:text-fg-1'
                    }`}
                >
                    <FiKey className="inline mr-2" />
                    Global API Keys
                </button>
            </div>

            {/* Exchange Configuration Section */}
            {/* Cards on a phone, table on desktop. */}
            {expandedSection === 'exchanges' && isPhone && renderExchangeCards()}

            {expandedSection === 'exchanges' && !isPhone && (
                <div className="space-y-4">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-[var(--divider)]">
                            <thead className="bg-bg-1">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                                        Exchange
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                                        Validation Method
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                                        Configuration
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-bg-2 divide-y divide-[var(--divider)]">
                                {exchanges.map((exchange) => (
                                    <tr key={exchange.exchange_code} className="hover:bg-bg-3/50">
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="text-sm font-medium text-fg-1">
                                                {exchange.exchange_code}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <select
                                                value={exchange.validation_method}
                                                onChange={(e) => handleExchangeUpdate(exchange.exchange_code, 'validation_method', e.target.value)}
                                                className="ds-input"
                                            >
                                                <option value="broker_api">Broker API</option>
                                                <option value="global_api">Global API</option>
                                                <option value="calendar_only">Calendar Only</option>
                                            </select>
                                        </td>
                                        <td className="px-4 py-3">
                                            {renderConfigControl(exchange, 'ds-input')}
                                        </td>
                                        <td className="px-4 py-3">
                                            {renderEnabledToggle(exchange, true)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <button
                                                type="button"
                                                onClick={() => saveExchangeConfig(exchange.exchange_code)}
                                                disabled={saving}
                                                className="btn btn-primary btn-sm inline-flex items-center"
                                            >
                                                <FiSave className="mr-1.5" />
                                                Save
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* API Keys Section */}
            {expandedSection === 'api-keys' && (
                <div className="space-y-4">
                    {apiCredentials.map((credential) => (
                        <div key={credential.provider} className="border border-[var(--divider)] rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center">
                                    <FiKey className="text-fg-3 mr-2" />
                                    <h4 className="text-sm font-semibold text-fg-1 capitalize">
                                        {credential.provider.replace('_', ' ')}
                                    </h4>
                                </div>
                                <div className="flex items-center">
                                    {credential.is_configured ? (
                                        <span className="ds-badge sm profit">
                                            <FiCheckCircle size={13} />
                                            Configured
                                        </span>
                                    ) : (
                                        <span className="ds-badge sm neutral">
                                            Not Configured
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="flex-1 flex items-center border border-[var(--divider)] rounded-md px-3 py-2 bg-bg-3">
                                    <input
                                        type={showApiKeys[credential.provider] ? "text" : "password"}
                                        value={decryptedKeys[credential.provider] !== undefined 
                                            ? decryptedKeys[credential.provider] 
                                            : (showApiKeys[credential.provider] ? "" : "")}
                                        placeholder={credential.masked_key || `Enter ${credential.provider} API key`}
                                        onChange={(e) => {
                                            // Allow typing new key
                                            setDecryptedKeys(prev => ({ ...prev, [credential.provider]: e.target.value }));
                                        }}
                                        className="flex-1 text-sm bg-transparent text-fg-1 focus:outline-none font-mono"
                                    />
                                    {/* 44px tap target — it used to be a bare 16px glyph with only
                                        ml-2. The negative margins let it eat into the field's own
                                        padding, so the target grows without the field getting taller. */}
                                    {credential.masked_key && (
                                        <button
                                            type="button"
                                            aria-label={showApiKeys[credential.provider] ? 'Hide API key' : 'Show API key'}
                                            onClick={() => handleToggleApiKey(credential.provider)}
                                            className="-my-2 -mr-2 ml-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-fg-2 hover:text-fg-1 transition-colors"
                                        >
                                            {showApiKeys[credential.provider]
                                                ? <FiEyeOff className="w-4 h-4" />
                                                : <FiEye className="w-4 h-4" />
                                            }
                                        </button>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        const newKey = decryptedKeys[credential.provider];
                                        if (newKey) {
                                            handleAPIKeyUpdate(credential.provider, newKey);
                                            setDecryptedKeys(prev => ({ ...prev, [credential.provider]: null }));
                                            setShowApiKeys(prev => ({ ...prev, [credential.provider]: false }));
                                        }
                                    }}
                                    disabled={saving || !decryptedKeys[credential.provider]}
                                    className="btn btn-primary btn-sm inline-flex items-center"
                                >
                                    <FiSave className="mr-1.5" />
                                    {credential.masked_key ? 'Update' : 'Save'}
                                </button>
                            </div>
                            {credential.masked_key && !showApiKeys[credential.provider] && (
                                <p className="mt-2 text-xs text-fg-2">
                                    Current: {credential.masked_key}
                                </p>
                            )}
                            {credential.last_validated_at && (
                                <p className="mt-2 text-xs text-fg-2">
                                    Last validated: {new Date(credential.last_validated_at).toLocaleString()}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ExchangeSettingsPanel;
